import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { Toaster } from '@/components/ui/toaster';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import Dashboard from '@/pages/Dashboard';
import SubscriptionPage from '@/pages/SubscriptionPage';
import SubscriptionSuccessPage from '@/pages/SubscriptionSuccessPage';
import OpsCenter from '@/pages/OpsCenter';
import SuppliesStore from '@/pages/SuppliesStore';
import FrequenciesPage from '@/pages/FrequenciesPage';
import FrequencyIndexPage from '@/pages/FrequencyIndexPage';
import PricingPage from '@/pages/PricingPage';
import CommandCenter from '@/pages/CommandCenter';
import ProfilePage from '@/pages/ProfilePage';
import AdminPanel from '@/pages/AdminPanel';
import SafetyIntelligencePage from '@/pages/SafetyIntelligencePage';
import { StateFrequenciesPage, CityFrequenciesPage } from '@/pages/SEOFrequencyPages';
import '@/App.css';

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Auth redirect wrapper
const AuthRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route 
        path="/auth" 
        element={
          <AuthRoute>
            <AuthPage />
          </AuthRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription" 
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription/success" 
        element={
          <ProtectedRoute>
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ops" 
        element={
          <ProtectedRoute>
            <OpsCenter />
          </ProtectedRoute>
        } 
      />
      <Route path="/supplies" element={<SuppliesStore />} />
      <Route path="/supplies/*" element={<SuppliesStore />} />
      <Route path="/frequencies" element={<FrequenciesPage />} />
      <Route path="/frequencies/browse" element={<FrequencyIndexPage />} />
      <Route path="/frequencies/:stateCode" element={<StateFrequenciesPage />} />
      <Route path="/frequencies/:stateCode/:citySlug" element={<CityFrequenciesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/command-center" element={<ProtectedRoute><CommandCenter /></ProtectedRoute>} />
      <Route path="/overwatch" element={<Navigate to="/dashboard" replace />} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/safety" element={<ProtectedRoute><SafetyIntelligencePage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <SubscriptionProvider>
              <NotificationProvider>
                <AppRoutes />
                <Toaster />
              </NotificationProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
